/**
 * Seed data for the media prototype. Only `@/api/mediaApi` reads this file.
 *
 * Field names follow the tables they will come from: `patients` / `account_user`
 * for the roster, `orders_diagnosis` for diagnoses, and the planned media tables
 * (`media_item`, `media_set`, `media_progression`, `media_ref`) for the rest. A
 * field with no column yet is marked "no column". Dates are offsets from now, so
 * the seed never goes stale. The images under /media-proto/ are generated
 * illustrations, not patient photos.
 */

export const ACCOUNT = 'ACCT-DEMO';

// The session actor while mocked. In production it comes from useSessionSelfStore().profile.
export const ME = { unique_ID: 'U-JSMITH', name: 'Jennifer Smith, RN' };

export const PEOPLE = {
  'U-JSMITH': 'Jennifer Smith, RN',
  'U-SPARKER': 'Dr. Susan Parker',
  'U-MREYES': 'Michael Reyes, PT',
};

// patients columns: pid, psersonalID (unique_ID), first_name, lastname, date_of_birth, gender.
export const PATIENTS = [
  { pid: 1346, unique_ID: 'P-JDOE', first_name: 'John', lastname: 'Doe', date_of_birth: '1946-02-14', gender: 'Male' },
  { pid: 1347, unique_ID: 'P-MWILSON', first_name: 'Mary', lastname: 'Wilson', date_of_birth: '1951-07-02', gender: 'Female' },
  { pid: 1348, unique_ID: 'P-RKIM', first_name: 'Robert', lastname: 'Kim', date_of_birth: '1939-11-23', gender: 'Male' },
];

// orders_diagnosis rows. `label` has no column: the description source is not found yet.
export const DIAGNOSES = {
  'P-JDOE': [
    { orddiagid: 88, diagnosis_code: 'S80.12XA', label: 'Contusion of left lower leg' },
    { orddiagid: 89, diagnosis_code: 'E11.9', label: 'Type 2 diabetes mellitus' },
    { orddiagid: 90, diagnosis_code: 'L89.152', label: 'Pressure ulcer of sacral region, stage 2' },
  ],
  'P-MWILSON': [
    { orddiagid: 91, diagnosis_code: 'S40.011A', label: 'Contusion of right shoulder' },
    { orddiagid: 92, diagnosis_code: 'L30.9', label: 'Dermatitis, unspecified' },
  ],
  'P-RKIM': [
    { orddiagid: 93, diagnosis_code: 'S70.02XA', label: 'Contusion of left hip' },
  ],
};

// media_primary_tag and media_body_site seed rows. Body sites are also the Body map regions.
export const PRIMARY_TAGS = [
  { code: 'skin', label: 'Skin / Integumentary' },
  { code: 'musculoskeletal', label: 'Musculoskeletal' },
  { code: 'cardiovascular', label: 'Cardiovascular' },
  { code: 'respiratory', label: 'Respiratory' },
  { code: 'neurological', label: 'Neurological' },
  { code: 'wound', label: 'Wound / Post-op' },
  { code: 'other', label: 'Other' },
];

export const BODY_SITES = [
  'head', 'face', 'neck', 'chest', 'back', 'abdomen', 'hip', 'left_arm', 'right_arm', 'left_hand', 'right_hand',
  'left_leg', 'right_leg', 'left_foot', 'right_foot', 'sacrum', 'left_heel', 'right_heel',
].map((code) => ({ code, label: code.replace('_', ' ').replace(/^./, (c) => c.toUpperCase()) }));

// media_setting: retention mocked at 10 years, uploader remove window 24 h.
export const SETTINGS = { retention_years: 10, uploader_remove_window_sec: 24 * 3600 };

const DAY = 864e5;
const at = (daysAgo, hh = 9, mm = 0) => {
  const d = new Date(Date.now() - daysAgo * DAY);
  d.setHours(hh, mm, 0, 0);
  return d.toISOString();
};
const dateOnly = (iso) => iso.slice(0, 10);
const src = (f) => `/media-proto/${f}`;

/** Builds the seed, fresh each time (ids are stable, dates move with the clock). */
export function buildSeed() {
  const items = [];
  const sets = [];
  const progressions = [];
  let id = 100;
  const item = (p, fields) => {
    id += 1;
    const row = {
      id,
      public_id: `seed${String(id).padStart(28, '0')}`,
      accountnumber: ACCOUNT,
      patient_pid: p ? p.pid : null,
      patient_unique_id: p ? p.unique_ID : null,
      author_unique_id: 'U-JSMITH',
      captured_by_unique_id: null,
      kind: 'image',
      content_type: 'image/svg+xml',
      width: 960,
      height: 720,
      duration_sec: null,
      capture_source: 'camera',
      note: '',
      primary_tag: 'skin',
      tags: [],
      body_site: null,
      body_site_detail: null,
      orddiagid: null,
      patient_visible: 0,
      removed_at: null,
      removed_by_unique_id: null,
      remove_reason: null,
      ...fields,
    };
    row.created_at =new Date(new Date(row.captured_at).getTime() + 8 * 60e3).toISOString();
    // Seed files are static; uploads carry `src: null` and are served from IndexedDB.
    row.src = fields.src || null;
    row.thumb_src = fields.thumb_src || fields.src || null;
    items.push(row);
    return row;
  };
  const set = (p, name, members, fields = {}) => {
    const earliest = members.map((m) => m.captured_at).sort()[0];
    const row = {
      id: sets.length + 10,
      accountnumber: ACCOUNT,
      patient_pid: p.pid,
      patient_unique_id: p.unique_ID,
      owner_unique_id: 'U-JSMITH',
      name,
      set_date: dateOnly(earliest),
      progression_id: null,
      created_at: earliest,
      removed_at: null,
      items: members.map((m, i) => ({ media_id: m.id, position: i + 1 })),
      ...fields,
    };
    sets.push(row);
    return row;
  };
  const progression = (p, name, fields) => {
    const row = {
      id: progressions.length + 1,
      accountnumber: ACCOUNT,
      patient_pid: p.pid,
      patient_unique_id: p.unique_ID,
      owner_unique_id: 'U-JSMITH',
      name,
      created_at: at(40),
      removed_at: null,
      ...fields,
    };
    progressions.push(row);
    return row;
  };

  const [doe, wilson, kim] = PATIENTS;

  // John Doe: left leg bruise, five weekly sets forming one progression.
  const bruiseProg = progression(doe, 'Left leg bruise progression', { primary_tag: 'skin', body_site: 'left_leg' });
  const notes = [
    'Initial presentation after fall at home. Dark purple, tender to touch.',
    'Swelling reduced. Colour still deep purple centrally.',
    'Edges turning green-yellow. Patient reports less pain.',
    'Mostly yellow-green, smaller area.',
    'Bruising on left leg, much improved compared to last week.',
  ];
  [28, 21, 14, 7, 0].forEach((daysAgo, stage) => {
    const members = [1, 2].map((shot) => item(doe, {
      captured_at: at(daysAgo, 8 + shot, 30 + shot * 7),
      src: src(`bruise-${stage + 1}-${shot}.svg`),
      note: shot === 1 ? notes[stage] : '',
      tags: ['bruise', 'left leg'],
      body_site: 'left_leg',
      body_site_detail: 'lateral shin',
      orddiagid: 88,
      patient_visible: stage >= 3 ? 1 : 0,
      author_unique_id: stage === 2 ? 'U-SPARKER' : 'U-JSMITH',
    }));
    set(doe, stage === 4 ? 'Left leg bruise' : `Left leg bruise — week ${stage + 1}`, members, { progression_id: bruiseProg.id });
  });

  // John Doe: sacral pressure area, three checks, one progression.
  const ulcerProg = progression(doe, 'Sacral pressure area', { primary_tag: 'wound', body_site: 'sacrum' });
  [20, 10, 3].forEach((daysAgo, i) => {
    const m = item(doe, {
      captured_at: at(daysAgo, 15, 10),
      src: src(`ulcer-${i + 1}.svg`),
      note: ['Stage 2, 3 cm. Repositioning every 2 h started.', 'Healing edges, 2 cm.', 'Nearly closed. Continue offloading.'][i],
      primary_tag: 'wound',
      tags: ['pressure ulcer', 'sacrum'],
      body_site: 'sacrum',
      orddiagid: 90,
    });
    set(doe, `Pressure area check ${i + 1}`, [m], { progression_id: ulcerProg.id });
  });

  // John Doe: right ankle wound, a set with no progression yet.
  const ankle = [1, 2, 3].map((i) => item(doe, {
    captured_at: at(5, 11, i * 4),
    src: src(`wound-${i}.svg`),
    note: i === 1 ? 'Superficial abrasion right ankle from bed rail.' : '',
    primary_tag: 'wound',
    tags: ['abrasion', 'right ankle'],
    body_site: 'right_foot',
    author_unique_id: 'U-MREYES',
  }));
  set(doe, 'Right ankle wound', ankle);

  // John Doe: loose items in no set, including two seed videos (posters only; no bytes in the seed).
  const gait = item(doe, {
    kind: 'video', content_type: 'video/mp4', duration_sec: 45, captured_at: at(2, 10, 5),
    src: null, thumb_src: src('poster-gait.svg'), seed_without_bytes: true,
    note: 'Mobility assessment: gait with walker.', primary_tag: 'musculoskeletal', tags: ['gait', 'walker'],
    author_unique_id: 'U-MREYES', capture_source: 'camera',
  });
  item(doe, {
    kind: 'video', content_type: 'video/mp4', duration_sec: 72, captured_at: at(9, 16, 40),
    src: null, thumb_src: src('poster-breath.svg'), seed_without_bytes: true,
    note: 'Breathing at rest, mild use of accessory muscles.', primary_tag: 'respiratory', tags: ['breathing'],
  });
  // A message attachment stays in its conversation (message_attachment → stored_file, domain
  // 'message') until someone saves it to the patient's media, so these are not media items.
  const rashFiles = [1, 2].map((i) => ({
    stored_file_id: 7000 + i, kind: 'image', content_type: 'image/svg+xml', src: src(`rash-${i}.svg`),
    captured_at: at(12, 9, i * 3), sender_unique_id: 'U-JSMITH', note: i === 1 ? 'Forearm rash after new detergent.' : '',
    tags: ['rash', 'left arm'], body_site: 'left_arm', saved_media_id: null,
  }));

  // Mary Wilson and Robert Kim: enough for cross-patient search.
  const arm = [1, 2].map((i) => item(wilson, {
    captured_at: at(6, 13, i * 5), src: src(`arm-${i}.svg`), note: i === 1 ? 'Bruise right upper arm after fall.' : '',
    tags: ['bruise', 'right arm'], body_site: 'right_arm', orddiagid: 91,
  }));
  set(wilson, 'Arm bruise — fall', arm);
  const hip = item(kim, {
    captured_at: at(30, 10, 0), src: src('hip-1.svg'), note: 'Hip bruising, both sides, after transfer.',
    tags: ['bruise', 'hip'], body_site: 'hip', orddiagid: 93, author_unique_id: 'U-SPARKER',
  });

  // Personal library: not attached to a patient.
  item(null, {
    captured_at: at(15, 12, 0), src: src('rash-2.svg'), capture_source: 'gallery',
    note: 'Reference photo: contact dermatitis pattern (teaching).', tags: ['rash', 'reference'],
  });

  // Notes and messages that reference media. `media_ref` freezes the item list.
  // patientNotes columns: pid, patientId, pnotes, postDate, type, provsignature.
  const lastBruise = sets.find((s) => s.name === 'Left leg bruise');
  const sources = [
    {
      source_type: 'note', source_id: 5521, patient_unique_id: doe.unique_ID,
      postDate: lastBruise.created_at, provsignature: 'Jennifer Smith, RN', type: 'Progress',
      pnotes: 'Home visit. Bruising on left leg much improved. Photos attached for comparison with last week.',
      frozen_item_ids: lastBruise.items.map((i) => i.media_id), set_id: lastBruise.id,
    },
    {
      source_type: 'note', source_id: 5510, patient_unique_id: doe.unique_ID,
      postDate: at(5, 11, 30), provsignature: 'Michael Reyes, PT', type: 'Therapy',
      pnotes: 'Abrasion right ankle noted during transfer training. Padded the bed rail. See photos.',
      frozen_item_ids: ankle.map((i) => i.id), set_id: null,
    },
    {
      source_type: 'note', source_id: 5498, patient_unique_id: doe.unique_ID,
      postDate: at(2, 10, 20), provsignature: 'Michael Reyes, PT', type: 'Therapy',
      pnotes: 'Gait assessment with walker, 20 m. Video attached.',
      frozen_item_ids: [gait.id], set_id: null,
    },
    {
      source_type: 'message', source_id: 9012, patient_unique_id: doe.unique_ID,
      postDate: at(12, 9, 30), provsignature: 'Jennifer Smith, RN', type: 'Message to Dr. Susan Parker',
      pnotes: 'Dr. Parker, new rash on his forearm since Monday. Worth a look at the next visit?',
      frozen_item_ids: [], set_id: null, message_files: rashFiles,
    },
    {
      source_type: 'note', source_id: 6020, patient_unique_id: kim.unique_ID,
      postDate: hip.created_at, provsignature: 'Dr. Susan Parker', type: 'Progress',
      pnotes: 'Bilateral hip bruising after transfer. Review transfer technique with aides.',
      frozen_item_ids: [hip.id], set_id: null,
    },
  ];

  return { items, sets, progressions, sources, reassignLog: [], nextId: 1000 };
}
